/* ddi-product-link.js
   Hydrator for <a class="ddi-product-link" data-handle="..." href="/products/...">
   Desktop (hover-capable pointer) only: on hover/focus fetch /products/{handle}.js
   and show a floating card with image, title, rating (if data-rating supplied),
   price, and a "View product" CTA. Mobile/touch: no hover; the anchor is a plain
   working link to the product page (untouched by this script).

   Retrofit (F-20260805-22): also promote raw /products/ anchors inside the
   article body's richtext containers (.ddi-ab-text, .ddi-ab-callout__body) to
   ddi-product-link + data-handle so the hovercard binds to them too. Shopify's
   richtext sanitizer strips class/data-* on <a>, so the compiler-emitted
   attributes never survive; this recovers them client-side without touching
   the compiler, section schema, or any article template. */
(function () {
  if (window.__ddiProductLinkInit) return;
  window.__ddiProductLinkInit = true;

  // No hover on touch: bail early. The anchor remains a plain link.
  var mq = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)');
  if (!mq || !mq.matches) return;

  var CACHE = Object.create(null);   // handle -> product JSON promise
  var currentAnchor = null;
  var openTimer = 0;
  var closeTimer = 0;
  var card = null;
  var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD';

  // Selector for the sanitizer-safe hydration targets. Only richtext containers
  // inside article-body sections - product-grid cards and PDP anchors are out
  // of scope (they have their own visual affordances).
  var HYDRATE_SELECTOR =
    '.ddi-article-body .ddi-ab-text a[href^="/products/"],' +
    '.ddi-article-body .ddi-ab-callout__body a[href^="/products/"]';

  function formatMoney(cents) {
    if (cents === null || cents === undefined) return '';
    var amount = (cents / 100);
    // Match DDI convention: "$239" (no trailing .00 for whole prices) - falls back to 2dp otherwise.
    var whole = Math.abs(amount - Math.round(amount)) < 0.005;
    return '$' + (whole ? Math.round(amount).toLocaleString('en-AU') : amount.toFixed(2));
  }

  function ensureCard() {
    if (card) return card;
    card = document.createElement('div');
    card.className = 'ddi-product-link-card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-hidden', 'true');
    card.innerHTML = '<div class="ddi-product-link-card__loading">Loading...</div>';
    // Keep the card open when the pointer moves onto it.
    card.addEventListener('mouseenter', function () { cancelClose(); });
    card.addEventListener('mouseleave', function () { scheduleClose(); });
    document.body.appendChild(card);
    return card;
  }

  function getProduct(handle) {
    if (CACHE[handle]) return CACHE[handle];
    var url = '/products/' + encodeURIComponent(handle) + '.js';
    CACHE[handle] = fetch(url, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .catch(function () { CACHE[handle] = null; return null; });
    return CACHE[handle];
  }

  function starPct(rating) {
    var r = parseFloat(rating);
    if (!isFinite(r) || r <= 0) return null;
    if (r > 5) r = 5;
    return (r / 5 * 100).toFixed(1) + '%';
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  // Extract the product handle from a /products/<handle>[/<variant>][?query][#hash]
  // href. Returns '' if no valid handle is present.
  function handleFromHref(href) {
    if (!href) return '';
    var m = /^\/products\/([^\/?#]+)/.exec(href);
    return m ? decodeURIComponent(m[1]) : '';
  }

  function renderCard(product, anchor) {
    if (!card) return;
    if (!product) {
      // Silent fail: leave the anchor as a plain link, don't show a broken card.
      card.removeAttribute('data-visible');
      card.setAttribute('aria-hidden', 'true');
      return;
    }
    var rating = anchor.getAttribute('data-rating');
    var ratingCount = anchor.getAttribute('data-rating-count');
    var pct = starPct(rating);
    var img = product.featured_image || (product.images && product.images[0]) || '';
    if (img && img.indexOf('//') === 0) img = 'https:' + img;
    // Ask for a specific width so the CDN returns a right-sized asset.
    if (img && img.indexOf('cdn.shopify.com') !== -1 && img.indexOf('_x') === -1) {
      img = img.replace(/(\.(jpg|jpeg|png|webp))(\?|$)/i, '_600x$1$3');
    }
    var title = product.title || anchor.textContent || '';
    var price = product.price;
    var compare = product.compare_at_price;
    var onSale = compare && compare > price;
    var soldOut = product.available === false;
    var url = product.url || anchor.getAttribute('href') || '#';

    var ratingHtml = '';
    if (pct) {
      ratingHtml = '<div class="ddi-product-link-card__meta">' +
        '<span class="ddi-product-link-card__stars" aria-hidden="true" style="--pct:' + pct + '"></span>' +
        '<span class="ddi-product-link-card__ratingnum">' + esc(parseFloat(rating).toFixed(1)) + '</span>' +
        (ratingCount ? '<span>(' + esc(ratingCount) + ')</span>' : '') +
        '</div>';
    }

    var priceHtml;
    if (soldOut) {
      priceHtml = '<div class="ddi-product-link-card__price"><span class="ddi-product-link-card__soldout">Sold out</span></div>';
    } else if (onSale) {
      priceHtml = '<div class="ddi-product-link-card__price">' +
        '<span>' + esc(formatMoney(price)) + '</span>' +
        '<span class="ddi-product-link-card__price-was">' + esc(formatMoney(compare)) + '</span>' +
        '<span class="ddi-product-link-card__price-badge">Save</span>' +
        '</div>';
    } else {
      priceHtml = '<div class="ddi-product-link-card__price"><span>' + esc(formatMoney(price)) + '</span></div>';
    }

    card.innerHTML =
      (img ? '<img class="ddi-product-link-card__img" src="' + esc(img) + '" alt="" loading="lazy" width="280" height="210">' : '') +
      '<div class="ddi-product-link-card__body">' +
        '<p class="ddi-product-link-card__title">' + esc(title) + '</p>' +
        ratingHtml +
        priceHtml +
        '<a class="ddi-product-link-card__cta" href="' + esc(url) + '">View product</a>' +
      '</div>';
    card.setAttribute('aria-hidden', 'false');
    card.setAttribute('data-visible', '1');
  }

  function positionCard(anchor) {
    if (!card) return;
    var rect = anchor.getBoundingClientRect();
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var cw = card.offsetWidth || 280;
    var ch = card.offsetHeight || 240;
    var margin = 8;

    // Preferred: below the anchor, left-aligned to it.
    var left = rect.left;
    var top = rect.bottom + margin;

    // Flip up if it would overflow bottom.
    if (top + ch > vh - 8 && rect.top - margin - ch > 8) {
      top = rect.top - margin - ch;
    }
    // Clamp horizontally to viewport with 8px gutter.
    if (left + cw > vw - 8) left = vw - cw - 8;
    if (left < 8) left = 8;
    // Final vertical clamp so it's never off-screen.
    if (top < 8) top = 8;
    if (top + ch > vh - 8) top = Math.max(8, vh - ch - 8);

    card.style.left = Math.round(left) + 'px';
    card.style.top = Math.round(top) + 'px';
  }

  function open(anchor) {
    currentAnchor = anchor;
    ensureCard();
    card.innerHTML = '<div class="ddi-product-link-card__loading">Loading...</div>';
    positionCard(anchor);
    // Show the shell immediately (loading state), then fill.
    card.setAttribute('data-visible', '1');
    card.setAttribute('aria-hidden', 'false');
    var handle = anchor.getAttribute('data-handle');
    if (!handle) { close(); return; }
    getProduct(handle).then(function (product) {
      // Guard: user may have moved to a different link before this resolved.
      if (currentAnchor !== anchor) return;
      renderCard(product, anchor);
      // Re-position now that the card has real dimensions.
      positionCard(anchor);
    });
  }

  function close() {
    currentAnchor = null;
    if (card) {
      card.removeAttribute('data-visible');
      card.setAttribute('aria-hidden', 'true');
    }
  }

  function scheduleOpen(anchor) {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    openTimer = setTimeout(function () { open(anchor); }, 120);
  }

  function scheduleClose() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    closeTimer = setTimeout(function () { close(); }, 180);
  }

  function cancelClose() {
    clearTimeout(closeTimer);
  }

  function bind(anchor) {
    if (anchor.__ddiPlBound) return;
    anchor.__ddiPlBound = true;
    anchor.addEventListener('mouseenter', function () { scheduleOpen(anchor); });
    anchor.addEventListener('mouseleave', function () { scheduleClose(); });
    anchor.addEventListener('focus', function () { scheduleOpen(anchor); });
    anchor.addEventListener('blur', function () { scheduleClose(); });
  }

  // Promote raw <a href="/products/..."> anchors inside article-body richtext
  // containers to ddi-product-link + data-handle so the same bind path picks
  // them up. Idempotent: skips anchors that are already promoted or bound.
  function hydrate(root) {
    var scope = root || document;
    // querySelectorAll on element or document; both support the same API.
    var nodes = scope.querySelectorAll ? scope.querySelectorAll(HYDRATE_SELECTOR) : [];
    for (var i = 0; i < nodes.length; i++) {
      var a = nodes[i];
      if (a.__ddiPlHydrated) continue;
      var handle = handleFromHref(a.getAttribute('href') || '');
      if (!handle) continue;
      a.__ddiPlHydrated = true;
      if (!a.classList.contains('ddi-product-link')) a.classList.add('ddi-product-link');
      if (!a.getAttribute('data-handle')) a.setAttribute('data-handle', handle);
    }
  }

  function scan(root) {
    var scope = root || document;
    hydrate(scope);
    scope.querySelectorAll('a.ddi-product-link[data-handle]').forEach(bind);
  }

  function init() {
    scan();
    // Watch for compiler-injected anchors added after initial paint.
    if (window.MutationObserver) {
      var mo = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var m = muts[i];
          for (var j = 0; j < m.addedNodes.length; j++) {
            var n = m.addedNodes[j];
            if (n.nodeType !== 1) continue;
            // Direct hits.
            if (n.matches && n.matches('a.ddi-product-link[data-handle]')) bind(n);
            if (n.matches && n.matches(HYDRATE_SELECTOR)) { hydrate(n.parentNode || document); bind(n); }
            // Subtree scan (covers new .ddi-ab-text containers added dynamically).
            if (n.querySelectorAll) scan(n);
          }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
    // Keep the card anchored to its link on scroll/resize while it is open.
    window.addEventListener('scroll', function () { if (currentAnchor) positionCard(currentAnchor); }, { passive: true });
    window.addEventListener('resize', function () { if (currentAnchor) positionCard(currentAnchor); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
